import {
  Component,
  ChangeDetectionStrategy,
  forwardRef,
  viewChild,
  input,
  booleanAttribute,
  inject,
  output,
  afterNextRender,
  DestroyRef,
  Injector,
  ElementRef,
  effect,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  PristineChangeEvent,
  ReactiveFormsModule,
  TouchedChangeEvent,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { translation } from '@cue/translations';
import { FloatingLabelComponent } from '@progress/kendo-angular-label';
import { CueDatetimepickerComponent } from '../datetimepicker/cue-datetimepicker/cue-datetimepicker.component';
import {
  CueDateTimePickerIncrementalSteps,
  CueDateTimePickerPopupSettings,
} from '../datetimepicker/cue-datetimepicker/cue-datetimepicker.types';

@Component({
  selector: 'assist-timepicker',
  templateUrl: './timepicker.component.html',
  styleUrl: './timepicker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CueDatetimepickerComponent, ReactiveFormsModule, TranslocoPipe],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimepickerComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TimepickerComponent),
      multi: true,
    },
  ],
})
export class TimepickerComponent implements ControlValueAccessor, Validator {
  private readonly floatingLabel = inject(FloatingLabelComponent, { optional: true });
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  showClear = input<boolean, boolean | string>(false, {
    transform: booleanAttribute,
  });
  inputFocus = output<FocusEvent>();
  readonly value = input<Date | null>();
  readonly valueCommit = output<Date | null>();
  readonly enterKeyBehavior = input<'open' | 'blur'>('open');
  steps = input<CueDateTimePickerIncrementalSteps>();
  format = input('HH:mm');
  appendTo = input<CueDateTimePickerPopupSettings['appendTo']>('component');
  private readonly picker = viewChild.required(CueDatetimepickerComponent);
  min = input<Date>();
  max = input<Date>();
  popupClass = input('assist-timepicker-popup');
  popupOrigin = input<ElementRef<HTMLElement>>();
  translation = translation;
  private onChange: (value: Date | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private onValidatorChange: () => void = () => undefined;
  readonly _control = new FormControl<Date | null>(null);

  constructor() {
    effect(() => {
      const value = this.value();
      if (value !== undefined) {
        untracked(() => this.writeValue(value));
      }
    });
    this._control.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => this.onChange(value));
    this._control.statusChanges.pipe(takeUntilDestroyed()).subscribe(() => this.onValidatorChange());
    afterNextRender(() => {
      const parent = this.injector.get(NgControl, null, { optional: true, self: true })?.control;
      if (!parent) {
        return;
      }
      parent.touched
        ? this._control.markAsTouched({ emitEvent: false })
        : this._control.markAsUntouched({ emitEvent: false });
      parent.dirty
        ? this._control.markAsDirty({ emitEvent: false })
        : this._control.markAsPristine({ emitEvent: false });
      parent.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
        if (event instanceof TouchedChangeEvent) {
          event.touched
            ? this._control.markAsTouched({ emitEvent: false })
            : this._control.markAsUntouched({ emitEvent: false });
        } else if (event instanceof PristineChangeEvent) {
          event.pristine
            ? this._control.markAsPristine({ emitEvent: false })
            : this._control.markAsDirty({ emitEvent: false });
        }
      });
    });
  }

  open() {
    this.picker().toggle(true);
  }

  focus(event: FocusEvent) {
    this.inputFocus.emit(event);
    if (this.floatingLabel) {
      this.floatingLabel.focused = true;
    }
  }

  blur() {
    this.onTouched();
    if (this._control.valid) {
      this.valueCommit.emit(this._control.value);
    }
    if (this.floatingLabel) {
      this.floatingLabel.focused = false;
    }
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  writeValue(value: Date | null): void {
    this._control.setValue(value, { emitEvent: false });
  }

  setDisabledState(disabled: boolean): void {
    if (disabled && this._control.enabled) {
      this._control.disable({ emitEvent: false });
    } else if (!disabled && this._control.disabled) {
      this._control.enable({ emitEvent: false });
    }
  }

  validate(_: AbstractControl<Date | null>): ValidationErrors | null {
    return this._control.errors;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }
}
